import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, Upload, Database, Palette, AlertTriangle } from "lucide-react";

// Small utility to trigger a browser download from an arbitrary JS object.
const downloadJson = (obj, filename) => {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const readJsonFile = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => { try { resolve(JSON.parse(r.result)); } catch (e) { reject(e); } };
  r.onerror = reject;
  r.readAsText(file);
});

export default function Migrate() {
  const [busy, setBusy] = useState(null);
  const [replace, setReplace] = useState(false);
  const [snapshot, setSnapshot] = useState(null);

  const exportBranding = async () => {
    setBusy("brand-export");
    try {
      const { data } = await api.get("/admin/branding/export");
      downloadJson(data, `branding-${new Date().toISOString().slice(0, 10)}.json`);
      toast.success("Branding exported");
    } catch (e) { toast.error(e.response?.data?.detail || "Export failed"); }
    setBusy(null);
  };

  const importBranding = async (file) => {
    if (!file) return;
    setBusy("brand-import");
    try {
      const data = await readJsonFile(file);
      await api.post("/admin/branding/import", { data });
      toast.success("Branding applied — refresh to see changes");
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Import failed"); }
    setBusy(null);
  };

  const exportSnapshot = async () => {
    setBusy("snap-export");
    try {
      const { data } = await api.get("/admin/snapshot/export");
      setSnapshot(data);
      downloadJson(data, `snapshot-${new Date().toISOString().slice(0, 10)}.json`);
      const totals = Object.entries(data.collections || {}).map(([k, v]) => `${k}:${v.length}`).join(", ");
      toast.success(`Snapshot ready · ${totals}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Export failed"); }
    setBusy(null);
  };

  const importSnapshot = async (file) => {
    if (!file) return;
    if (replace && !window.confirm("REPLACE mode will WIPE and re-seed each collection with the imported data. Continue?")) return;
    setBusy("snap-import");
    try {
      const data = await readJsonFile(file);
      const { data: res } = await api.post("/admin/snapshot/import", { data, replace });
      toast.success("Snapshot imported — refresh to see data");
      setSnapshot({ imported: true, summary: res.summary });
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Import failed"); }
    setBusy(null);
  };

  const btn = "px-4 py-2.5 rounded-lg font-bold text-sm inline-flex items-center gap-2 disabled:opacity-50 transition-colors";

  return (
    <div className="space-y-6" data-testid="migrate-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-[color:var(--accent)]">Admin</div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Data Migration</h1>
        <p className="text-sm text-slate-400 mt-2 max-w-2xl">
          Move your branding and content between environments (preview ↔ production, or after a fresh deploy).
          Export from the source, then import on the target.
        </p>
      </div>

      {/* Branding block — quick + small payload */}
      <section className="card-surface p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Palette size={18} className="text-[color:var(--accent)]" />
          <h2 className="text-lg font-bold">Branding</h2>
        </div>
        <p className="text-sm text-slate-400">
          Company name, tagline, logo (base64), accent colors, login hero image. This is the fastest way to fix
          a fresh production deploy that came up with defaults.
        </p>
        <div className="flex flex-wrap gap-3">
          <button onClick={exportBranding} disabled={busy === "brand-export"} data-testid="branding-export" className={btn} style={{ background: "var(--accent)", color: "#000" }}>
            <Download size={14} /> {busy === "brand-export" ? "Exporting…" : "Export branding"}
          </button>
          <label className={`${btn} border border-[#2a3447] text-slate-200 hover:border-[color:var(--accent)] cursor-pointer`}>
            <Upload size={14} /> {busy === "brand-import" ? "Importing…" : "Import JSON"}
            <input type="file" accept="application/json,.json" hidden onChange={(e) => importBranding(e.target.files?.[0])} data-testid="branding-import" />
          </label>
        </div>
      </section>

      {/* Full snapshot block */}
      <section className="card-surface p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Database size={18} className="text-[color:var(--accent)]" />
          <h2 className="text-lg font-bold">Full data snapshot</h2>
        </div>
        <p className="text-sm text-slate-400">
          Users (without password hashes), announcements, KB articles, incident templates, incidents, taxonomies,
          documents, events, courses, task templates, shift swaps, shifts, time off, tasks, kudos, polls, and
          onboarding tasks. Passwords are never exported — invite new users or send a password reset instead.
        </p>

        <label className="flex items-start gap-3 p-3 rounded-lg bg-[#0b0d12] border border-[#2a3447] text-sm cursor-pointer" data-testid="replace-toggle-wrap">
          <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} data-testid="replace-toggle" className="mt-0.5 accent-[color:var(--accent)]" />
          <div>
            <div className="font-semibold flex items-center gap-2">
              Replace mode <AlertTriangle size={13} className={replace ? "text-[#ff9f1c]" : "text-slate-600"} />
            </div>
            <div className="text-xs text-slate-400 mt-0.5">
              {replace
                ? "Wipe each collection before inserting. Use for a clean seed of a fresh environment."
                : "Upsert by id — safe default, keeps records that aren't in the file."}
            </div>
          </div>
        </label>

        <div className="flex flex-wrap gap-3">
          <button onClick={exportSnapshot} disabled={busy === "snap-export"} data-testid="snapshot-export" className={btn} style={{ background: "var(--accent)", color: "#000" }}>
            <Download size={14} /> {busy === "snap-export" ? "Exporting…" : "Export snapshot"}
          </button>
          <label className={`${btn} border border-[#2a3447] text-slate-200 hover:border-[color:var(--accent)] cursor-pointer`}>
            <Upload size={14} /> {busy === "snap-import" ? "Importing…" : "Import snapshot"}
            <input type="file" accept="application/json,.json" hidden onChange={(e) => importSnapshot(e.target.files?.[0])} data-testid="snapshot-import" />
          </label>
        </div>

        {snapshot && (
          <div className="mt-3 text-xs text-slate-400 bg-[#0b0d12] border border-[#2a3447] rounded-lg p-3 font-mono max-h-48 overflow-auto" data-testid="snapshot-summary">
            {snapshot.imported ? (
              <pre>{JSON.stringify(snapshot.summary, null, 2)}</pre>
            ) : (
              <pre>{Object.entries(snapshot.collections || {}).map(([k, v]) => `${k}: ${v.length}`).join("\n")}</pre>
            )}
          </div>
        )}
      </section>

      <div className="text-xs text-slate-500 max-w-2xl">
        Tip: to copy from preview → production, open Migration on the preview URL, click <b>Export snapshot</b>,
        then open Migration on your live domain, tick <b>Replace mode</b>, and upload the file.
      </div>
    </div>
  );
}
